import React, { useState, useCallback } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { ZoomOut } from 'lucide-react';
import { PM_THRESHOLDS } from '../constants/pmThresholds';
import { buildDayBoundaryAnnotations } from './chartAnnotations';
import { formatDateLocale } from '../utils/formatters';
import useChartZoomWindow from './useChartZoomWindow';
import AnalyzeZoomButton from './AnalyzeZoomButton';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, zoomPlugin, annotationPlugin);

/**
 * CombinedChart — Graphique combiné PM + Température + Humidité
 *
 * 3 axes Y :
 *   yLeft   — PM1, PM2.5, PM10 (µg/m³) avec seuils de référence
 *   yRight  — Température (°C)
 *   yRight2 — Humidité (%)
 *
 * Toggle de chaque courbe via les cases à cocher.
 * Zoom molette/pinch, panoramique sans touche + bouton reset (useChartZoomWindow).
 */

// Couleurs des courbes
const COLORS = {
  pm1:         { border: 'rgba(0,206,209,1)',    bg: 'rgba(0,206,209,0.08)'   },
  pm25:        { border: 'rgba(147,112,219,1)',  bg: 'rgba(147,112,219,0.12)' },
  pm10:        { border: 'rgba(255,140,0,1)',    bg: 'rgba(255,140,0,0.1)'    },
  temperature: { border: 'rgba(239,68,68,1)',    bg: 'rgba(239,68,68,0.08)'   },
  humidity:    { border: 'rgba(59,130,246,1)',   bg: 'rgba(59,130,246,0.08)'  },
};

const COLORS_DARK = {
  pm1:         { border: 'rgba(64,224,208,0.9)',  bg: 'rgba(64,224,208,0.08)'  },
  pm25:        { border: 'rgba(186,135,255,0.9)', bg: 'rgba(186,135,255,0.1)'  },
  pm10:        { border: 'rgba(255,183,77,0.9)',  bg: 'rgba(255,183,77,0.08)'  },
  temperature: { border: 'rgba(248,113,113,0.9)', bg: 'rgba(248,113,113,0.07)' },
  humidity:    { border: 'rgba(96,165,250,0.9)',  bg: 'rgba(96,165,250,0.07)'  },
};

const SERIES = [
  { key: 'pm1',         label: 'PM1',                            axis: 'yLeft',   unit: 'µg/m³' },
  { key: 'pm25',        label: 'PM2.5',                          axis: 'yLeft',   unit: 'µg/m³' },
  { key: 'pm10',        label: 'PM10',                           axis: 'yLeft',   unit: 'µg/m³' },
  { key: 'temperature', labelKey: 'charts.series.temperature',   axis: 'yRight',  unit: '°C'   },
  { key: 'humidity',    labelKey: 'charts.series.humidity',      axis: 'yRight2', unit: '%'    },
];

// Seuils à afficher sur l'axe PM (lignes pointillées) — libellés localisés dans le composant
const PM_THRESHOLD_LINES = [
  { value: PM_THRESHOLDS.pm25.warningMax, pm: 'PM2.5', kind: 'warn',  color: 'rgba(239,159,39,0.6)' },
  { value: PM_THRESHOLDS.pm25.alertMax,   pm: 'PM2.5', kind: 'alert', color: 'rgba(216,90,48,0.6)'  },
  { value: PM_THRESHOLDS.pm10.warningMax, pm: 'PM10',  kind: 'warn',  color: 'rgba(255,183,77,0.5)' },
];

function getReadingValue(r, key) {
  if (key === 'pm25') return r.pm2_5;
  return r[key];
}

function formatLabel(r) {
  // Label d'axe X (affichage) : heure « HH:MM » localisée (FR/EN identiques, colon).
  return formatDateLocale(r.timestamp, { hour: '2-digit', minute: '2-digit' });
}

function CombinedChart({ readings, darkMode = false, onQuickAnnotate, onAnalyzeZoom }) {
  const { t } = useTranslation();
  // Panoramique sans touche modificatrice, comportement d'origine conservé.
  const zoom = useChartZoomWindow({ readings, onAnalyzeZoom });
  const chartRef = zoom.chartRef;
  const colors = darkMode ? COLORS_DARK : COLORS;
  // PM10 jamais mesuré (ex. Atmotube Pro 2, D194) : ni série, ni légende, ni
  // ligne de seuil PM10 — une courbe vide à côté d'un seuil laisserait croire
  // à une mesure sous le seuil.
  const hasPm10 = (readings || []).some(r => typeof r.pm10 === 'number' && Number.isFinite(r.pm10));
  // Séries et lignes de seuil localisées (labels traduits, valeurs/PM invariants)
  const series = SERIES
    .filter(s => hasPm10 || s.key !== 'pm10')
    .map(s => ({ ...s, label: s.labelKey ? t(s.labelKey) : s.label }));
  const thresholdLines = PM_THRESHOLD_LINES.filter(l => hasPm10 || l.pm !== 'PM10').map(l => ({
    ...l,
    label: t(`charts.threshold.${l.kind}`, { pm: l.pm, v: l.value }),
  }));
  const [quickAnnot, setQuickAnnot] = useState(null);

  const handleChartClick = useCallback((event) => {
    if (!onQuickAnnotate || !chartRef.current) return;
    const chart = chartRef.current;
    const rect  = chart.canvas.getBoundingClientRect();
    const xPos  = event.clientX - rect.left;
    const yPos  = event.clientY - rect.top;
    const elements = chart.getElementsAtEventForMode(event.nativeEvent || event,
      'nearest', { intersect: false }, false);
    if (!elements.length) { setQuickAnnot(null); return; }
    const idx = elements[0].index;
    const reading = readings[idx];
    if (!reading) return;
    setQuickAnnot({ x: xPos, y: Math.max(40, yPos - 60), timestamp: reading.timestamp });
  }, [onQuickAnnotate, readings, chartRef]);

  // État de visibilité de chaque série
  const [visible, setVisible] = useState({
    pm1: true, pm25: true, pm10: true, temperature: true, humidity: true,
  });

  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));

  if (!readings?.length) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: darkMode ? '#94A3B8' : '#4A5568' }}>
        {t('charts.empty.generic')}
      </div>
    );
  }

  const labels = readings.map(formatLabel);

  // Calcul des plages Y pour chaque axe
  const pmValues   = readings.flatMap(r => [r.pm1, r.pm2_5, r.pm10]).filter(v => v != null);
  const tempValues = readings.map(r => r.temperature).filter(v => v != null);

  const pmMax   = pmValues.length   ? Math.max(...pmValues)   * 1.2 : 100;
  const tempMin = tempValues.length ? Math.min(...tempValues) - 3   : 0;
  const tempMax = tempValues.length ? Math.max(...tempValues) + 3   : 40;

  // Datasets — un par série
  const datasets = series.map(({ key, label, axis }) => {
    const c = colors[key];
    const isPM = axis === 'yLeft';
    return {
      label,
      data: readings.map(r => getReadingValue(r, key)),
      borderColor: c.border,
      backgroundColor: c.bg,
      borderWidth: isPM ? 2 : 1.5,
      pointRadius: 0,
      pointHoverRadius: 4,
      fill: false,
      tension: 0.3,
      yAxisID: axis,
      hidden: !visible[key],
    };
  });

  // Lignes seuils PM en datasets (type line, no legend, no tooltip)
  const thresholdDatasets = thresholdLines.map(({ value, label, color }) => ({
    label,
    data: readings.map(() => value),
    borderColor: color,
    borderWidth: 1,
    borderDash: [5, 4],
    pointRadius: 0,
    fill: false,
    yAxisID: 'yLeft',
    hidden: !visible.pm25 && !visible.pm10,
  }));

  const c = {
    text:    darkMode ? '#E1E1E1' : '#2C3E50',
    textSec: darkMode ? '#94A3B8' : '#4A5568',
    grid:    darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    card:    darkMode ? '#282845' : '#FFFFFF',
    border:  darkMode ? 'rgba(255,255,255,0.08)' : '#E0E0E0',
  };

  const dayAnnotations = buildDayBoundaryAnnotations(readings, darkMode)
    .reduce((obj, ann, i) => { obj[`day${i}`] = ann; return obj; }, {});

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false }, // légende custom ci-dessous
      annotation: { annotations: dayAnnotations },
      tooltip: {
        backgroundColor: darkMode ? '#1E1E32' : '#FFFFFF',
        titleColor: c.text,
        bodyColor: c.textSec,
        borderColor: c.border,
        borderWidth: 1,
        callbacks: {
          label: (ctx) => {
            const serie = series.find(s => s.label === ctx.dataset.label);
            if (!serie || ctx.parsed.y == null) return null;
            return ` ${ctx.dataset.label} : ${ctx.parsed.y.toFixed(1)} ${serie.unit}`;
          },
        },
      },
      zoom: zoom.zoomOptions,
    },
    scales: {
      x: {
        ticks: { color: c.textSec, maxTicksLimit: 12, font: { size: 11 } },
        grid:  { color: c.grid },
      },
      yLeft: {
        type: 'linear', position: 'left',
        min: 0, max: pmMax,
        title: { display: true, text: 'PM (µg/m³)', color: c.textSec, font: { size: 11 } },
        ticks: { color: c.textSec, font: { size: 11 } },
        grid:  { color: c.grid },
      },
      yRight: {
        type: 'linear', position: 'right',
        min: tempMin, max: tempMax,
        title: { display: true, text: t('charts.axis.temperature'), color: COLORS.temperature.border, font: { size: 11 } },
        ticks: { color: COLORS.temperature.border, font: { size: 11 } },
        grid:  { drawOnChartArea: false },
      },
      yRight2: {
        type: 'linear', position: 'right',
        min: 0, max: 100,
        offset: true,
        title: { display: true, text: t('charts.axis.humidity'), color: COLORS.humidity.border, font: { size: 11 } },
        ticks: { color: COLORS.humidity.border, font: { size: 11 } },
        grid:  { drawOnChartArea: false },
      },
    },
  };

  return (
    <div style={{ background: c.card, borderRadius: '12px', border: `1px solid ${c.border}`, padding: '20px', marginBottom: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: c.text, fontFamily: "'Poppins', sans-serif" }}>
          {t('charts.combined.title')}
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
        <AnalyzeZoomButton zoom={zoom} />
        <button
          type="button" onClick={zoom.resetZoom}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px',
            border: `1px solid ${c.border}`, background: 'transparent', color: c.textSec,
            cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' }}
        >
          <ZoomOut size={13} /> {t('common.zoom.reset')}
        </button>
        </div>
      </div>

      {/* Légende custom avec toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        {series.map(({ key, label, unit }) => {
          const col = colors[key].border;
          const active = visible[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
                border: `1px solid ${active ? col : c.border}`,
                background: active ? `${col}18` : 'transparent',
                color: active ? col : c.textSec,
                fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ width: '10px', height: '3px', background: active ? col : c.border, borderRadius: '2px', display: 'inline-block' }} />
              {label}
              <span style={{ fontSize: '10px', opacity: 0.7 }}>{unit}</span>
            </button>
          );
        })}
      </div>

      {/* Graphique */}
      <div style={{ position: 'relative', height: '340px' }} onClick={handleChartClick}>
        <Line
          ref={chartRef}
          data={{ labels, datasets: [...datasets, ...thresholdDatasets] }}
          options={options}
        />
        {quickAnnot && onQuickAnnotate && (
          <div style={{
            position: 'absolute', left: quickAnnot.x, top: quickAnnot.y,
            transform: 'translateX(-50%)',
            background: darkMode ? '#1E1E32' : '#fff',
            border: '1.5px solid #1B77F1', borderRadius: '8px', padding: '8px 12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10,
            whiteSpace: 'nowrap', pointerEvents: 'all'
          }}>
            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>
              {formatDateLocale(quickAnnot.timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={(e) => { e.stopPropagation(); onQuickAnnotate(quickAnnot.timestamp); setQuickAnnot(null); }}
                style={{ padding: '4px 10px', borderRadius: '6px', background: '#1B77F1', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                {t('session.annotations.quickAdd')}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setQuickAnnot(null); }}
                aria-label={t('common.close')}
                style={{ padding: '4px 8px', borderRadius: '6px', background: 'none',
                  border: '1px solid #e0e0e0', cursor: 'pointer', fontSize: '12px', color: '#6B7280' }}>
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Légende seuils */}
      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {thresholdLines.map(({ label, color }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: c.textSec }}>
            <span style={{ width: '18px', height: '1px', borderTop: `2px dashed ${color}`, display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: c.textSec, margin: '8px 0 0', fontStyle: 'italic', lineHeight: '1.5' }}>
        {t('charts.combined.footnote')}
      </p>
    </div>
  );
}

export default React.memo(CombinedChart, (prev, next) =>
  prev.readings === next.readings &&
  prev.darkMode === next.darkMode &&
  prev.onAnalyzeZoom === next.onAnalyzeZoom
);
